import { useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { CAMPAIGN_CUSTOMER_CASES, type CustomerCase } from "@/data/campaign-customer-cases";
import type { ReactNode } from "react";
import { SmartlaneButton } from "@/components/SmartlaneButton";

type Props = {
  onNavigate?: (segment: string, customer: string, method: string) => void;
  cases?: CustomerCase[];
  eyebrow?: string;
  heading?: ReactNode;
  intro?: ReactNode;
  className?: string;
  /** Full-bleed photo cards without the side text column. */
  imageOnly?: boolean;
  /** "section" matches the orange dash + mono eyebrow used by the other chapter headings. */
  eyebrowStyle?: "utility" | "section";
};

export function CustomerCaseSlider({ onNavigate, cases = CAMPAIGN_CUSTOMER_CASES, eyebrow, heading, intro, className, imageOnly = false, eyebrowStyle = "utility" }: Props = {}) {
  const CAMPAIGN_CUSTOMER_CASES = cases;
  const [active, setActive] = useState(0);
  const touchStart = useRef<number | null>(null);
  const count = CAMPAIGN_CUSTOMER_CASES.length;
  const select = (index: number, method = "pfeil") => {
    const next = (index + count) % count;
    setActive(next);
    const item = CAMPAIGN_CUSTOMER_CASES[next];
    if (item) onNavigate?.(item.segment, item.customer, method);
  };
  const current = CAMPAIGN_CUSTOMER_CASES[active];
  if (!current) return null;

  return (
    <section className={className ?? "customer-section overflow-hidden bg-surface-soft py-12 md:py-16 xl:py-20"} aria-labelledby="customer-cases-heading">
      <div className="container-wide">
        {eyebrowStyle === "section" ? (
          <div className="mb-5 inline-flex items-center gap-3">
            <span className="h-px w-10 bg-smartlane-orange" />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-smartlane-orange">
              {eyebrow ?? "10+ JAHRE IN DER DISPOSITION ZU HAUSE"}
            </span>
          </div>
        ) : (
          <p className="eyebrow">{eyebrow ?? "10+ JAHRE IN DER DISPOSITION ZU HAUSE"}</p>
        )}
        <h2 id="customer-cases-heading" className={`${eyebrowStyle === "section" ? "" : "mt-3 "}text-display-md text-rich-black`}>
          {heading ?? <>UNTERSCHIEDLICHE BRANCHEN.<br />EIN GEMEINSAMES ZIEL: EFFIZIENTE TOUREN JEDEN TAG.</>}
        </h2>
        <p className="copy mt-5 max-w-3xl text-base text-muted-foreground md:text-lg">
          {intro ?? "Von komplexem Stückgut bis zur Filialbelieferung: Smartlane Transport Intelligence automatisiert zeitaufwendige Planungsschritte, entlastet die Disposition und schafft mehr Zeit für das Wesentliche. Profitieren auch Sie!"}
        </p>
      </div>

      <div className="customer-stage mx-auto mt-6 max-w-[1680px]" role="region" aria-roledescription="Karussell" aria-label="Kundenbeispiele"


        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
          event.preventDefault();
          select(active + (event.key === "ArrowRight" ? 1 : -1), "tastatur");
        }}
        onTouchStart={(event) => { touchStart.current = event.touches[0]?.clientX ?? null; }}
        onTouchEnd={(event) => {
          const end = event.changedTouches[0]?.clientX;
          if (touchStart.current !== null && end !== undefined && Math.abs(touchStart.current - end) > 50) {
            select(active + (touchStart.current > end ? 1 : -1), "wischen");
          }
          touchStart.current = null;
        }}>
        <SmartlaneButton type="button" variant="ghost" size="sm" aria-label="Vorheriges Kundenbeispiel" title="Vorheriges Kundenbeispiel"
          onClick={() => select(active - 1)} className="customer-nav customer-nav-prev h-12 w-12 shrink-0 rounded-full border border-border bg-card p-0 shadow-lg">
          <ArrowLeft className="h-5 w-5" />
        </SmartlaneButton>
        <SmartlaneButton type="button" variant="ghost" size="sm" aria-label="Nächstes Kundenbeispiel" title="Nächstes Kundenbeispiel"
          onClick={() => select(active + 1)} className="customer-nav customer-nav-next h-12 w-12 shrink-0 rounded-full border border-border bg-card p-0 shadow-lg">
          <ArrowRight className="h-5 w-5" />
        </SmartlaneButton>

        {CAMPAIGN_CUSTOMER_CASES.map((item, index) => {
          const relative = (index - active + count) % count;
          const rawPosition = relative === count - 1 ? -1 : relative;
          const position = rawPosition > 2 ? "hidden" : rawPosition;
          const selected = position === 0;
          return (
            <article key={item.customer} className={imageOnly ? "customer-case customer-case-image" : "customer-case"} data-position={position}
              aria-hidden={!selected} aria-roledescription="Folie" aria-label={`${index + 1} von ${count}: ${item.segment} – ${item.customer}`}>
              {imageOnly ? (
                <div className="flex h-full flex-col">
                <div className={item.quote ? "relative aspect-[3/2] w-full overflow-hidden bg-muted" : "relative min-h-0 w-full flex-1 overflow-hidden bg-muted"}>
                  <img src={item.image} alt={selected ? item.imageAlt : ""} loading="lazy" decoding="async" className={item.imageFit === "contain" ? "h-full w-full object-contain" : "h-full w-full object-cover"} />
                  {item.logoPanel && (
                    <div className="absolute inset-0 flex items-center justify-center bg-card/60 p-8">
                      <img src={item.logo} alt={item.customer} loading="lazy" decoding="async" className="max-h-[70%] w-auto max-w-[70%] object-contain drop-shadow-lg" />
                    </div>
                  )}
                  {!item.hideCaption && (
                    <div className="absolute bottom-0 left-0 bg-card/95 px-5 py-3">
                      <p className="font-heading text-2xl uppercase leading-none text-card-foreground">{item.caption ?? item.segment}</p>
                    </div>
                  )}
                </div>
                {item.quote && (
                  <figure className="customer-case-copy border-t border-border px-5 py-4 md:px-6">
                    <blockquote className="text-sm leading-relaxed text-card-foreground">„{item.quote}“</blockquote>
                    <figcaption className="mt-1.5 text-xs text-muted-foreground">{item.author} · {item.customer}</figcaption>
                  </figure>
                )}
                </div>
              ) : (
              <div className="customer-case-layout grid h-full min-w-0 grid-rows-[140px_minmax(0,1fr)] md:grid-cols-[minmax(0,0.38fr)_minmax(0,0.62fr)] md:grid-rows-1">
                  <div className="relative min-w-0 overflow-hidden bg-muted">
                  {item.logoPanel ? (
                    <div className="flex h-full w-full items-center justify-center p-10 md:p-14">
                      <img src={item.image} alt={selected ? item.imageAlt : ""} loading="lazy" decoding="async"
                        className="w-full max-w-[320px]" />
                    </div>
                  ) : (
                    <img src={item.image} alt={selected ? item.imageAlt : ""} loading="lazy" decoding="async"
                      className="h-full w-full object-cover" />
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-card/95 px-5 py-3">
                    <p className="font-heading text-2xl uppercase leading-none text-card-foreground">{item.caption ?? item.segment}</p>
                  </div>
                </div>
                <div className="customer-case-copy flex min-h-0 min-w-0 flex-col p-4 md:p-6 xl:p-8">
                  <img src={item.logo} alt={item.customer} className="h-9 w-36 shrink-0 self-start object-contain object-left grayscale" loading="lazy" />
                  <h3 className="mt-4 font-heading text-2xl uppercase leading-tight text-card-foreground md:text-3xl">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{item.description}</p>
                  {item.quote && (
                    <figure className="mt-auto pt-5">
                      <div className="border-t border-border pt-3">
                      <blockquote className="text-sm leading-relaxed text-card-foreground">„{item.quote}“</blockquote>
                      <figcaption className="mt-2 text-xs text-muted-foreground">{item.author}</figcaption>
                      </div>
                    </figure>
                  )}
                </div>
              </div>
              )}
              {!selected && position !== 2 && position !== "hidden" && (
                <SmartlaneButton type="button" variant="ghost" tabIndex={-1}
                  aria-label={`${item.segment}: ${item.customer} anzeigen`} onClick={() => select(index, "karte")}
                  className="absolute inset-0 h-full w-full p-0 hover:bg-transparent">
                  <span className="sr-only">{item.segment} anzeigen</span>
                </SmartlaneButton>
              )}
            </article>
          );
        })}
      </div>

      <p className="sr-only" aria-live="polite" aria-atomic="true">{current.segment}: {current.customer}.</p>
    </section>
  );
}